import { useState, useRef, useEffect } from 'react'
import { Link } from 'react-router'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { api, Activity, ActivityBody, ApiChild, FamilyMember, MemberRole } from '@/lib/api'
import { EDUCATIONAL_AREAS, AREA_COLORS, areaColor, EducationalAreaValue } from '@/lib/constants'
import { useAuthStore } from '@/stores/authStore'
import { useFamilyStore } from '@/stores/familyStore'

// ── Image compression ─────────────────────────────────────────────────────────

const MAX_DIMENSION = 1600
const JPEG_QUALITY = 0.85

function compressImage(file: File): Promise<File> {
  return new Promise((resolve, reject) => {
    const img = new Image()
    const objectUrl = URL.createObjectURL(file)
    img.onload = () => {
      URL.revokeObjectURL(objectUrl)
      let { width, height } = img
      if (width > MAX_DIMENSION || height > MAX_DIMENSION) {
        if (width >= height) {
          height = Math.round((height / width) * MAX_DIMENSION)
          width = MAX_DIMENSION
        } else {
          width = Math.round((width / height) * MAX_DIMENSION)
          height = MAX_DIMENSION
        }
      }
      const canvas = document.createElement('canvas')
      canvas.width = width
      canvas.height = height
      canvas.getContext('2d')!.drawImage(img, 0, 0, width, height)
      canvas.toBlob(
        (blob) => {
          if (!blob) { reject(new Error('Image compression failed')); return }
          resolve(new File([blob], file.name.replace(/\.[^.]+$/, '.jpg'), { type: 'image/jpeg' }))
        },
        'image/jpeg',
        JPEG_QUALITY,
      )
    }
    img.onerror = () => { URL.revokeObjectURL(objectUrl); reject(new Error('Failed to load image')) }
    img.src = objectUrl
  })
}

// ── Constants ─────────────────────────────────────────────────────────────────

function areaLabel(value: string): string {
  return EDUCATIONAL_AREAS.find((a) => a.value === value)?.label ?? value
}

// ── Helpers ───────────────────────────────────────────────────────────────────

function formatDate(iso: string | null | undefined): string | null {
  if (!iso) return null
  const [y, m, d] = iso.split('-').map(Number)
  return new Date(y, m - 1, d).toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  })
}

// ── Sub-components ────────────────────────────────────────────────────────────

function Spinner() {
  return (
    <div className="flex items-center justify-center py-12">
      <div className="h-8 w-8 animate-spin rounded-full border-4 border-birch-200 border-t-birch-600" />
    </div>
  )
}

// ── ImageModal ────────────────────────────────────────────────────────────────

function ImageModal({
  images,
  initialIndex,
  onClose,
}: {
  images: string[]
  initialIndex: number
  onClose: () => void
}) {
  const [idx, setIdx] = useState(initialIndex)

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4"
      onClick={onClose}
    >
      <div
        className="relative max-h-full max-w-xl w-full"
        onClick={(e) => e.stopPropagation()}
      >
        <img
          src={images[idx]}
          alt={`Photo ${idx + 1}`}
          className="max-h-[80vh] w-full rounded-xl object-contain shadow-2xl"
        />
        {images.length > 1 && (
          <>
            <button
              onClick={(e) => { e.stopPropagation(); setIdx((i) => (i - 1 + images.length) % images.length) }}
              className="absolute left-2 top-1/2 -translate-y-1/2 flex h-8 w-8 items-center justify-center rounded-full bg-white/80 text-gray-700 shadow-md hover:bg-white text-lg"
            >
              ‹
            </button>
            <button
              onClick={(e) => { e.stopPropagation(); setIdx((i) => (i + 1) % images.length) }}
              className="absolute right-2 top-1/2 -translate-y-1/2 flex h-8 w-8 items-center justify-center rounded-full bg-white/80 text-gray-700 shadow-md hover:bg-white text-lg"
            >
              ›
            </button>
            <div className="mt-2 flex justify-center gap-1.5">
              {images.map((_, i) => (
                <button
                  key={i}
                  onClick={(e) => { e.stopPropagation(); setIdx(i) }}
                  className={`h-1.5 w-1.5 rounded-full ${i === idx ? 'bg-white' : 'bg-white/40'}`}
                />
              ))}
            </div>
          </>
        )}
        <button
          onClick={onClose}
          className="absolute -top-3 -right-3 flex h-8 w-8 items-center justify-center rounded-full bg-white text-gray-600 shadow-md hover:bg-gray-100"
          aria-label="Close"
        >
          ✕
        </button>
      </div>
    </div>
  )
}

// ── ActivityRow ───────────────────────────────────────────────────────────────

function ActivityRow({
  activity,
  familyId,
  childId,
  canEdit,
  canDelete,
}: {
  activity: Activity
  familyId: string
  childId: string
  canEdit: boolean
  canDelete: boolean
}) {
  const qc = useQueryClient()
  const [editing, setEditing] = useState(false)
  const [description, setDescription] = useState(activity.description)
  const [educationalAreas, setEducationalAreas] = useState<string[]>(activity.educationalAreas)
  const [isFeatured, setIsFeatured] = useState(activity.isFeatured)
  const [date, setDate] = useState(activity.date ?? '')
  const [notes, setNotes] = useState(activity.notes ?? '')
  const [error, setError] = useState<string | null>(null)
  const [confirmDelete, setConfirmDelete] = useState(false)
  const [lightbox, setLightbox] = useState<number | null>(null)

  const attachments = activity.mediaAttachments ?? []
  const isPending = activity.reviewStatus === 'pending'

  const { mutate: save, isPending: saving } = useMutation({
    mutationFn: () => {
      const body: ActivityBody = {
        description: description.trim(),
        educationalAreas,
        isFeatured,
        date: date || null,
        notes: notes.trim() || null,
      }
      return api.activities.update(familyId, childId, activity.id, body)
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['activities', familyId, childId] })
      setEditing(false)
      setError(null)
    },
    onError: (err: Error) => setError(err.message),
  })

  const { mutate: remove, isPending: removing } = useMutation({
    mutationFn: () => api.activities.delete(familyId, childId, activity.id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['activities', familyId, childId] })
    },
    onError: (err: Error) => setError(err.message),
  })

  function cancelEdit() {
    setEditing(false)
    setDescription(activity.description)
    setEducationalAreas(activity.educationalAreas)
    setIsFeatured(activity.isFeatured)
    setDate(activity.date ?? '')
    setNotes(activity.notes ?? '')
    setError(null)
  }

  if (editing) {
    return (
      <li className="overflow-hidden rounded-lg border border-birch-200 bg-birch-50 p-4">
        <form
          onSubmit={(e) => { e.preventDefault(); setError(null); save() }}
          className="space-y-3"
        >
          <div>
            <label className="mb-1 block text-xs font-medium text-gray-600">Description *</label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={3}
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-base focus:border-birch-500 focus:outline-none focus:ring-1 focus:ring-birch-500"
              required
              autoFocus
            />
          </div>
          <div>
            <label className="mb-1 block text-xs font-medium text-gray-600">Educational Areas *</label>
            <div className="flex flex-wrap gap-1.5">
              {EDUCATIONAL_AREAS.map(({ value, label }) => {
                const selected = educationalAreas.includes(value)
                return (
                  <button
                    key={value}
                    type="button"
                    onClick={() =>
                      setEducationalAreas((prev) =>
                        selected ? prev.filter((a) => a !== value) : [...prev, value],
                      )
                    }
                    className={`rounded-full px-2.5 py-1 text-xs font-medium transition-colors ${
                      selected
                        ? 'bg-birch-600 text-white'
                        : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                    }`}
                  >
                    {label}
                  </button>
                )
              })}
            </div>
          </div>
          <div className="grid gap-3 sm:grid-cols-2">
            <div className="min-w-0">
              <label className="mb-1 block text-xs font-medium text-gray-600">Date</label>
              <input
                type="date"
                value={date}
                onChange={(e) => setDate(e.target.value)}
                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-base focus:border-birch-500 focus:outline-none focus:ring-1 focus:ring-birch-500"
              />
            </div>
          </div>
          <div>
            <label className="mb-1 block text-xs font-medium text-gray-600">Notes</label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={2}
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-base focus:border-birch-500 focus:outline-none focus:ring-1 focus:ring-birch-500"
              placeholder="Optional observations or reflections…"
            />
          </div>
          <label className="flex items-center gap-2 text-sm text-gray-700">
            <input
              type="checkbox"
              checked={isFeatured}
              onChange={(e) => setIsFeatured(e.target.checked)}
              className="h-4 w-4 rounded border-gray-300 text-birch-600 focus:ring-birch-500"
            />
            Feature in portfolio
          </label>
          {error && <p className="text-sm text-red-600">{error}</p>}
          <div className="flex gap-2">
            <button
              type="submit"
              disabled={saving || !description.trim() || educationalAreas.length === 0}
              className="rounded-lg bg-birch-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-birch-700 disabled:opacity-50"
            >
              {saving
                ? (isPending ? 'Accepting…' : 'Saving…')
                : (isPending ? 'Accept' : 'Save')}
            </button>
            <button
              type="button"
              onClick={cancelEdit}
              className="rounded-lg px-3 py-1.5 text-sm text-gray-600 hover:bg-gray-100"
            >
              Cancel
            </button>
          </div>
        </form>
      </li>
    )
  }

  return (
    <li className="rounded-lg border border-gray-100 bg-white px-4 py-4 shadow-sm">
      <div className="flex items-start justify-between gap-3">
        <div className="flex min-w-0 flex-wrap items-center gap-2">
          {activity.educationalAreas.map((area) => (
            <span
              key={area}
              className={`inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide ${areaColor(area)}`}
            >
              {areaLabel(area)}
            </span>
          ))}
          {activity.isFeatured && (
            <span className="inline-flex items-center rounded-full bg-birch-100 px-2 py-0.5 text-[10px] font-semibold text-birch-700">
              Featured
            </span>
          )}
          {isPending && (
            <span className="inline-flex items-center rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-semibold text-amber-800">
              Needs review
            </span>
          )}
          {formatDate(activity.date) && (
            <span className="text-xs text-gray-400">{formatDate(activity.date)}</span>
          )}
        </div>
        <div className="flex shrink-0 items-center gap-1">
          {confirmDelete ? (
            <>
              <span className="text-xs text-gray-500">Remove?</span>
              <button
                onClick={() => remove()}
                disabled={removing}
                className="rounded px-2 py-1 text-xs font-medium text-red-600 hover:bg-red-50 disabled:opacity-50"
              >
                {removing ? 'Removing…' : 'Yes'}
              </button>
              <button
                onClick={() => setConfirmDelete(false)}
                className="rounded px-2 py-1 text-xs text-gray-500 hover:bg-gray-100"
              >
                Cancel
              </button>
            </>
          ) : (
            <>
              {canEdit && (
                <button
                  onClick={() => setEditing(true)}
                  className={`rounded px-2 py-1 text-xs hover:bg-gray-100 ${isPending ? 'font-semibold text-amber-700' : 'text-gray-600'}`}
                >
                  {isPending ? 'Review' : 'Edit'}
                </button>
              )}
              {canDelete && (
                <button
                  onClick={() => setConfirmDelete(true)}
                  className="rounded px-2 py-1 text-xs text-red-500 hover:bg-red-50"
                >
                  Remove
                </button>
              )}
            </>
          )}
        </div>
      </div>
      <p className="mt-1.5 text-sm text-gray-900">{activity.description}</p>
      {activity.notes && (
        <p className="mt-1 text-sm italic text-gray-500">"{activity.notes}"</p>
      )}
      {attachments.length > 0 && (
        <div className="mt-2">
          <div className="flex gap-2 overflow-x-auto pb-1">
            {attachments.map((url, i) => (
              <div key={i} className="relative h-36 w-36 shrink-0">
                <img
                  src={url}
                  alt={`Photo ${i + 1}`}
                  className="h-full w-full cursor-zoom-in rounded object-cover"
                  onClick={() => setLightbox(i)}
                  onError={(e) => { (e.target as HTMLImageElement).style.display = 'none' }}
                />
                {isPending && i === 0 && (
                  <div className="absolute bottom-0 left-0 right-0 rounded-b bg-amber-400/90 py-0.5 text-center text-[8px] font-bold uppercase tracking-wide text-amber-900">
                    Review
                  </div>
                )}
              </div>
            ))}
          </div>
          {attachments.length > 2 && (
            <div className="mt-1.5 flex items-center justify-center gap-1 sm:hidden">
              {attachments.map((_, i) => (
                <div
                  key={i}
                  className="h-1.5 w-1.5 rounded-full bg-gray-300"
                />
              ))}
              <span className="ml-1 text-[10px] text-gray-400">scroll →</span>
            </div>
          )}
        </div>
      )}
      {error && <p className="mt-1 text-xs text-red-600">{error}</p>}
      {lightbox !== null && (
        <ImageModal
          images={attachments}
          initialIndex={lightbox}
          onClose={() => setLightbox(null)}
        />
      )}
    </li>
  )
}

// ── AddActivityForm ───────────────────────────────────────────────────────────

type QueuedImage = { file: File; preview: string }

function AddActivityForm({ familyId, childId }: { familyId: string; childId: string }) {
  const qc = useQueryClient()
  const [open, setOpen] = useState(false)
  const [mode, setMode] = useState<'manual' | 'photo'>('photo')

  // Manual mode state
  const [description, setDescription] = useState('')
  const [educationalAreas, setEducationalAreas] = useState<string[]>([])
  const [isFeatured, setIsFeatured] = useState(false)
  const [date, setDate] = useState('')
  const [notes, setNotes] = useState('')

  // Photo mode state
  const [imageQueue, setImageQueue] = useState<QueuedImage[]>([])
  const [photoDate, setPhotoDate] = useState('')
  const [dragOver, setDragOver] = useState(false)
  const [compressing, setCompressing] = useState(false)
  const [uploadProgress, setUploadProgress] = useState<{ done: number; total: number } | null>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)

  const [error, setError] = useState<string | null>(null)

  function handleClose() {
    setOpen(false)
    setMode('photo')
    setDescription('')
    setEducationalAreas([])
    setIsFeatured(false)
    setDate('')
    setNotes('')
    imageQueue.forEach(({ preview }) => URL.revokeObjectURL(preview))
    setImageQueue([])
    setPhotoDate('')
    setDragOver(false)
    setCompressing(false)
    setUploadProgress(null)
    setError(null)
  }

  async function handleFiles(files: FileList | File[]) {
    const arr = Array.from(files).filter((f) => f.type.startsWith('image/'))
    if (!arr.length) return
    setCompressing(true)
    setError(null)
    try {
      const processed = await Promise.all(arr.map(compressImage))
      setImageQueue((prev) => [
        ...prev,
        ...processed.map((f) => ({ file: f, preview: URL.createObjectURL(f) })),
      ])
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not process image. Please use a JPEG or PNG.')
    } finally {
      setCompressing(false)
    }
  }

  function removeFromQueue(idx: number) {
    setImageQueue((prev) => {
      URL.revokeObjectURL(prev[idx].preview)
      return prev.filter((_, i) => i !== idx)
    })
  }

  const { mutate: create, isPending: creatingManual } = useMutation({
    mutationFn: () =>
      api.activities.create(familyId, childId, {
        description: description.trim(),
        educationalAreas,
        isFeatured,
        date: date || null,
        notes: notes.trim() || null,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['activities', familyId, childId] })
      handleClose()
      window.scrollTo({ top: 0, behavior: 'smooth' })
    },
    onError: (err: Error) => setError(err.message),
  })

  const { mutate: createFromImages, isPending: creatingFromImages } = useMutation({
    mutationFn: async () => {
      setUploadProgress({ done: 0, total: imageQueue.length })

      // Step 1: request presigned upload URLs
      const { uploadUrls } = await api.activities.getUploadUrls(
        familyId,
        childId,
        imageQueue.map((q) => q.file.name),
      )

      // Step 2: upload all images to S3 in parallel (no auth header)
      await Promise.all(
        uploadUrls.map(({ url }, i) =>
          fetch(url, {
            method: 'PUT',
            headers: { 'Content-Type': imageQueue[i].file.type || 'application/octet-stream' },
            body: imageQueue[i].file,
          }).then((res) => {
            if (!res.ok) throw new Error(`S3 upload failed (HTTP ${res.status})`)
            setUploadProgress((prev) => prev && { ...prev, done: prev.done + 1 })
          }),
        ),
      )

      // Step 3: create the activity with the uploaded S3 keys
      const activity = await api.activities.createFromImages(familyId, childId, {
        s3Keys: uploadUrls.map((u) => u.key),
        notes: notes.trim() || null,
        date: photoDate || null,
      })

      return [activity]
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['activities', familyId, childId] })
      handleClose()
      window.scrollTo({ top: 0, behavior: 'smooth' })
    },
    onError: (err: Error) => {
      setUploadProgress(null)
      setError(err.message)
    },
  })

  if (!open) {
    return (
      <button
        onClick={() => setOpen(true)}
        className="fixed bottom-6 right-6 z-40 flex h-14 w-14 items-center justify-center rounded-full bg-birch-600 text-white shadow-lg hover:bg-birch-700 active:scale-95 transition-transform"
        aria-label="Add activity"
      >
        <svg className="h-7 w-7" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M12 4.5v15m7.5-7.5h-15" />
        </svg>
      </button>
    )
  }

  const isPending = creatingManual || creatingFromImages || compressing

  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/40 sm:items-center sm:p-4" onClick={handleClose}>
    <div className="max-h-[90vh] w-full max-w-lg overflow-y-auto rounded-t-2xl bg-white p-5 shadow-xl sm:rounded-2xl" onClick={(e) => e.stopPropagation()}>
    <div className="space-y-3">
      <div className="space-y-2">
        <p className="text-center text-sm font-medium text-birch-800">Add an activity</p>
        <div className="flex rounded-lg border border-gray-200 bg-white p-0.5 text-sm">
          <button
            type="button"
            onClick={() => { setMode('photo'); setError(null) }}
            className={`flex-1 rounded-md px-4 py-2 font-medium transition-colors ${mode === 'photo' ? 'bg-birch-600 text-white' : 'text-gray-500 hover:text-gray-700'}`}
          >
            Upload photos
          </button>
          <button
            type="button"
            onClick={() => { setMode('manual'); setError(null) }}
            className={`flex-1 rounded-md px-4 py-2 font-medium transition-colors ${mode === 'manual' ? 'bg-birch-600 text-white' : 'text-gray-500 hover:text-gray-700'}`}
          >
            Enter details
          </button>
        </div>
      </div>

      {mode === 'manual' ? (
        <form
          onSubmit={(e) => { e.preventDefault(); setError(null); create() }}
          className="space-y-3"
        >
          <div>
            <label className="mb-1 block text-xs font-medium text-gray-600">Description *</label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={3}
              placeholder="Describe what was learned or accomplished…"
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-base focus:border-birch-500 focus:outline-none focus:ring-1 focus:ring-birch-500"
              required
              autoFocus
            />
          </div>
          <div>
            <label className="mb-1 block text-xs font-medium text-gray-600">Educational Areas *</label>
            <div className="flex flex-wrap gap-1.5">
              {EDUCATIONAL_AREAS.map(({ value, label }) => {
                const selected = educationalAreas.includes(value)
                return (
                  <button
                    key={value}
                    type="button"
                    onClick={() =>
                      setEducationalAreas((prev) =>
                        selected ? prev.filter((a) => a !== value) : [...prev, value],
                      )
                    }
                    className={`rounded-full px-2.5 py-1 text-xs font-medium transition-colors ${
                      selected
                        ? 'bg-birch-600 text-white'
                        : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                    }`}
                  >
                    {label}
                  </button>
                )
              })}
            </div>
          </div>
          <div>
            <label className="mb-1 block text-xs font-medium text-gray-600">Date</label>
            <input
              type="date"
              value={date}
              onChange={(e) => setDate(e.target.value)}
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-base focus:border-birch-500 focus:outline-none focus:ring-1 focus:ring-birch-500"
            />
          </div>
          <div>
            <label className="mb-1 block text-xs font-medium text-gray-600">Notes</label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={2}
              placeholder="Optional observations or reflections…"
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-base focus:border-birch-500 focus:outline-none focus:ring-1 focus:ring-birch-500"
            />
          </div>
          <label className="flex items-center gap-2 text-sm text-gray-700">
            <input
              type="checkbox"
              checked={isFeatured}
              onChange={(e) => setIsFeatured(e.target.checked)}
              className="h-4 w-4 rounded border-gray-300 text-birch-600 focus:ring-birch-500"
            />
            Feature in portfolio
          </label>
          {error && <p className="text-sm text-red-600">{error}</p>}
          <div className="flex gap-2">
            <button
              type="submit"
              disabled={isPending || !description.trim() || educationalAreas.length === 0}
              className="rounded-lg bg-birch-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-birch-700 disabled:opacity-50"
            >
              {creatingManual ? 'Adding…' : 'Add activity'}
            </button>
            <button
              type="button"
              onClick={handleClose}
              className="rounded-lg px-3 py-1.5 text-sm text-gray-600 hover:bg-gray-100"
            >
              Cancel
            </button>
          </div>
        </form>
      ) : (
        <div className="space-y-3">
          <input
            ref={fileInputRef}
            type="file"
            accept="image/jpeg,image/png"
            multiple
            onChange={(e) => {
              if (e.target.files) handleFiles(e.target.files)
              e.target.value = ''
            }}
            className="hidden"
          />

          {imageQueue.length === 0 && !compressing && (
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              onDragOver={(e) => { e.preventDefault(); setDragOver(true) }}
              onDragLeave={() => setDragOver(false)}
              onDrop={(e) => {
                e.preventDefault()
                setDragOver(false)
                if (e.dataTransfer.files) handleFiles(e.dataTransfer.files)
              }}
              className={`flex w-full flex-col items-center justify-center gap-2 rounded-lg border-2 border-dashed px-4 py-8 text-sm transition-colors ${
                dragOver
                  ? 'border-birch-500 bg-birch-50 text-birch-600'
                  : 'border-gray-300 text-gray-500 hover:border-birch-400 hover:text-birch-600'
              }`}
            >
              <span className="text-2xl">📷</span>
              <span className="sm:hidden">Tap to choose photos</span>
              <span className="hidden sm:inline">Drag & drop or click to choose photos</span>
              <span className="text-xs text-gray-400">Select multiple photos at once</span>
            </button>
          )}

          {compressing && (
            <div className="flex items-center justify-center gap-2 py-6 text-sm text-gray-500">
              <div className="h-4 w-4 animate-spin rounded-full border-2 border-gray-300 border-t-birch-600" />
              Processing photos…
            </div>
          )}

          {imageQueue.length > 0 && (
            <div>
              <div className="grid grid-cols-3 gap-2 sm:grid-cols-4">
                {imageQueue.map(({ preview }, i) => (
                  <div key={i} className="group relative aspect-square">
                    <img
                      src={preview}
                      alt={`Photo ${i + 1}`}
                      className="h-full w-full rounded-lg object-cover"
                    />
                    <button
                      type="button"
                      onClick={() => removeFromQueue(i)}
                      className="absolute -right-1.5 -top-1.5 flex h-5 w-5 items-center justify-center rounded-full bg-red-500 text-white text-xs shadow opacity-0 group-hover:opacity-100 transition-opacity"
                      aria-label="Remove photo"
                    >
                      ✕
                    </button>
                  </div>
                ))}
              </div>
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                onDragOver={(e) => { e.preventDefault(); setDragOver(true) }}
                onDragLeave={() => setDragOver(false)}
                onDrop={(e) => {
                  e.preventDefault()
                  setDragOver(false)
                  if (e.dataTransfer.files) handleFiles(e.dataTransfer.files)
                }}
                className="mt-2 w-full rounded-lg border border-dashed border-gray-300 py-1.5 text-xs text-gray-500 hover:border-birch-400 hover:text-birch-600"
              >
                + Add more photos
              </button>
            </div>
          )}

          <div>
            <label className="mb-1 block text-xs font-medium text-gray-600">Date</label>
            <input
              type="date"
              value={photoDate}
              onChange={(e) => setPhotoDate(e.target.value)}
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-base focus:border-birch-500 focus:outline-none focus:ring-1 focus:ring-birch-500"
            />
          </div>

          <div>
            <label className="mb-1 block text-xs font-medium text-gray-600">Notes</label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={2}
              placeholder="Optional observations or reflections…"
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-base focus:border-birch-500 focus:outline-none focus:ring-1 focus:ring-birch-500"
            />
          </div>

          {uploadProgress && (
            <p className="text-center text-sm text-gray-500">
              Uploading {uploadProgress.done} of {uploadProgress.total}…
            </p>
          )}

          {error && <p className="text-sm text-red-600">{error}</p>}

          <div className="flex gap-2">
            <button
              type="button"
              disabled={isPending || imageQueue.length === 0}
              onClick={() => { setError(null); createFromImages() }}
              className="rounded-lg bg-birch-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-birch-700 disabled:opacity-50"
            >
              {creatingFromImages
                ? `Uploading${uploadProgress ? ` ${uploadProgress.done}/${uploadProgress.total}` : '…'}`
                : `Upload & add${imageQueue.length > 1 ? ` (${imageQueue.length} photos)` : ''}`}
            </button>
            <button
              type="button"
              onClick={handleClose}
              className="rounded-lg px-3 py-1.5 text-sm text-gray-600 hover:bg-gray-100"
            >
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
    </div>
    </div>
  )
}

// ── Main Page ─────────────────────────────────────────────────────────────────

export function ActivitiesPage() {
  const { user } = useAuthStore()
  const { activeChildId } = useFamilyStore()

  // 1. Profile → familyId
  const { data: profile, isLoading: loadingProfile } = useQuery({
    queryKey: ['me'],
    queryFn: api.me.get,
  })

  const familyId = profile?.familyId ?? null

  // 2. Children
  const { data: children = [], isLoading: loadingChildren } = useQuery<ApiChild[]>({
    queryKey: ['children', familyId],
    queryFn: () => api.children.list(familyId!),
    enabled: !!familyId,
  })

  // 3. Members → role
  const { data: members = [] } = useQuery<FamilyMember[]>({
    queryKey: ['members', familyId],
    queryFn: () => api.members.list(familyId!),
    enabled: !!familyId,
  })

  // Resolve active child (prefer persisted selection, fall back to first)
  const activeChild: ApiChild | null =
    children.find((c) => c.id === activeChildId) ?? children[0] ?? null
  const childId = activeChild?.id ?? null

  const [areaFilter, setAreaFilter] = useState<EducationalAreaValue | null>(null)
  useEffect(() => { setAreaFilter(null) }, [childId])

  // 4. Activities
  const { data: activities, isLoading: loadingActivities } = useQuery<Activity[]>({
    queryKey: ['activities', familyId, childId, areaFilter],
    queryFn: () => api.activities.list(familyId!, childId!, areaFilter),
    enabled: !!familyId && !!childId,
  })

  const myRole: MemberRole | undefined = members.find(
    (m) => m.userId === user?.userId,
  )?.role
  const canEdit = myRole === 'owner' || myRole === 'contributor'
  const canDelete = myRole === 'owner'

  const sortedActivities = activities
    ? [...activities].sort((a, b) => (b.date ?? '').localeCompare(a.date ?? ''))
    : []

  const isLoading = loadingProfile || (!!familyId && loadingChildren)

  // ── No family ──────────────────────────────────────────────────────────────
  if (!isLoading && !familyId) {
    return (
      <div className="space-y-6">
        <h1 className="text-2xl font-bold text-gray-900">Activities</h1>
        <div className="rounded-xl border border-gray-200 bg-white p-8 text-center shadow-sm">
          <p className="text-gray-500">Set up your family first.</p>
          <Link
            to="/family"
            className="mt-3 inline-block rounded-lg bg-birch-600 px-4 py-2 text-sm font-medium text-white hover:bg-birch-700"
          >
            Go to Family
          </Link>
        </div>
      </div>
    )
  }

  // ── No children ────────────────────────────────────────────────────────────
  if (!isLoading && familyId && !activeChild) {
    return (
      <div className="space-y-6">
        <h1 className="text-2xl font-bold text-gray-900">Activities</h1>
        <div className="rounded-xl border border-gray-200 bg-white p-8 text-center shadow-sm">
          <p className="text-gray-500">Add a student in Family to start logging activities.</p>
          <Link
            to="/family"
            className="mt-3 inline-block rounded-lg bg-birch-600 px-4 py-2 text-sm font-medium text-white hover:bg-birch-700"
          >
            Go to Family
          </Link>
        </div>
      </div>
    )
  }

  return (
    <div className="min-w-0 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Activities</h1>
          {activeChild && (
            <p className="mt-0.5 text-sm text-gray-500">{activeChild.firstName}</p>
          )}
        </div>
        {!isLoading && !loadingActivities && activeChild && (
          <span className="text-sm text-gray-400">
            {sortedActivities.length} activit{sortedActivities.length !== 1 ? 'ies' : 'y'}
          </span>
        )}
      </div>

      <div className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm">
        <div className="p-6">
          {!isLoading && !loadingActivities && activeChild && (
            <div className="mb-4 -mx-1 flex gap-1.5 overflow-x-auto pb-1" style={{ scrollbarWidth: 'none' }}>
              <button
                onClick={() => setAreaFilter(null)}
                className={`shrink-0 rounded-full px-3 py-1 text-xs font-medium transition-colors ${
                  areaFilter === null
                    ? 'bg-birch-600 text-white'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                All
              </button>
              {EDUCATIONAL_AREAS.map(({ value, label }) => (
                <button
                  key={value}
                  onClick={() => setAreaFilter(areaFilter === value ? null : value)}
                  className={`shrink-0 rounded-full px-3 py-1 text-xs font-medium transition-colors ${
                    areaFilter === value
                      ? AREA_COLORS[value]
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  {label}
                </button>
              ))}
            </div>
          )}

          {(isLoading || loadingActivities) && <Spinner />}

          {!isLoading && !loadingActivities && sortedActivities.length === 0 && activeChild && (
            <p className="mb-4 text-sm text-gray-500">No activities logged yet.</p>
          )}

          {!isLoading && !loadingActivities && sortedActivities.length > 0 && (
            <ul className="mb-4 space-y-2">
              {sortedActivities.map((activity) => (
                <ActivityRow
                  key={activity.id}
                  activity={activity}
                  familyId={familyId!}
                  childId={childId!}
                  canEdit={canEdit}
                  canDelete={canDelete}
                />
              ))}
            </ul>
          )}

          {!isLoading && !loadingActivities && canEdit && activeChild && (
            <AddActivityForm familyId={familyId!} childId={childId!} />
          )}
        </div>
      </div>
    </div>
  )
}
